import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus, Send, Sparkles, RefreshCw, Trash2, ExternalLink, ChevronLeft, Upload,
  Copy, Linkedin, Eye, MousePointerClick, AlertCircle,
} from "lucide-react";
import { api } from "@/lib/api";

const LISTING_HOOK_DEFAULTS = {
  subject: "I re-marketed {{listing_address}} — 60-second preview",
  body: `Hi {{first_name}},

Saw your listing at {{listing_address}} and couldn't resist — I ran it through Chateaux to show you what your marketing kit could look like:

→ {{preview_url}}

What's on the page:
  • AI-staged hero photo
  • Full listing landing page with AI-written copy
  • Live AI concierge that answers buyer questions 24/7
  • One-tap Meta + Google ad kits ready to publish

Took 60 seconds. If you like what you see, hit reply.

— {{sender_name}}

---
Chateaux · {{compliance_address}}
Don't want emails like this? {{unsubscribe_url}}`,
  linkedin_template: `Hi {{first_name}} — played around with your {{listing_address}} listing on Chateaux (our AI marketing platform for agents). Here's what it built in 60s: {{preview_url}}

If you like it, happy to give you a full walk-through. If not, no worries.`,
};

const SUCCESS_REPORT_DEFAULTS = {
  subject: "{{first_name}}, the 3 agents between you and #1 in {{market_city}}",
  body: `Hi {{first_name}},

Spent 60 seconds crunching Zillow + Realtor.com data on the top agents in {{market_city}}. Here's your personalized breakdown — where you sit, the 3 agents ahead of you, and what they're doing that you aren't:

→ {{share_url}}

The report has 3 concrete moves you can steal this week + a 90-day catch-up plan. Zero fluff.

Chateaux made this in one click — that's kind of the point of the product. If it lands, hit reply and I'll show you the full app.

— {{sender_name}}

---
Chateaux · {{compliance_address}}
Don't want emails like this? {{unsubscribe_url}}`,
  linkedin_template: `Hi {{first_name}} — spent 60s reverse-engineering your competition in {{market_city}}. Sending you the top 3 agents ahead of you + the 3 things they're doing you aren't: {{share_url}}

If it's useful, hit reply, happy to walk through it. If not, no worries.`,
};

const STANDARD_DEFAULTS = {
  subject: "A faster way to market your listings",
  body: `Hi {{first_name}},

I built something for agents like you — Chateaux turns one listing photo into a staged hero image, an AI-written landing page, and ready-to-run Meta + Google ads in about 60 seconds.

Happy to show you a live demo. Just hit reply.

— {{sender_name}}

---
Chateaux · {{compliance_address}}
Don't want emails like this? {{unsubscribe_url}}`,
  linkedin_template: `Hi {{first_name}} — built an AI marketing platform for agents (Chateaux). Happy to give you a quick walk-through if useful.`,
};

const DEFAULTS_BY_TYPE = {
  listing_hook: LISTING_HOOK_DEFAULTS,
  success_report: SUCCESS_REPORT_DEFAULTS,
  standard: STANDARD_DEFAULTS,
};

const TYPE_LABEL = { listing_hook: "Listing hook", success_report: "Success report", standard: "Standard" };

const STATUS_STYLES = {
  pending: "bg-ink/10 text-ink/60",
  preview_ready: "bg-green-100 text-green-700",
  staging_failed: "bg-amber-100 text-amber-700",
  listing_url_invalid: "bg-red-100 text-red-700",
  sent: "bg-coral/15 text-coral",
};

const renderTokens = (text, contact, campaign) => {
  const share = contact.report_url || contact.preview_url || "";
  const map = {
    first_name: contact.first_name || "",
    last_name: contact.last_name || "",
    market_city: contact.city || contact.address || "",
    market: contact.city || contact.address || "",
    brokerage: contact.brokerage || "",
    listing_address: contact.address || "",
    share_url: share,
    preview_url: contact.preview_url || "",
    sender_name: campaign.sender_name || "Chateaux",
  };
  let out = text || "";
  Object.entries(map).forEach(([k, v]) => { out = out.replaceAll(`{{${k}}}`, v); });
  return out;
};

const parseCsv = (text) => {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length === 0) return [];
  let rows = lines;
  const hasHeader = lines[0].toLowerCase().includes("email") || lines[0].toLowerCase().includes("first_name");
  const cols = hasHeader ? lines[0].split(",").map((c) => c.trim().toLowerCase()) : null;
  if (hasHeader) rows = lines.slice(1);
  return rows.map((line) => {
    const parts = line.split(",").map((p) => p.trim());
    const get = (names, idx) => {
      if (cols) {
        for (const n of names) { const i = cols.indexOf(n); if (i >= 0) return parts[i] || ""; }
        return "";
      }
      return parts[idx] || "";
    };
    return {
      first_name: get(["first_name", "firstname"], 0),
      last_name: get(["last_name", "lastname"], 1),
      email: get(["email"], 2),
      linkedin_url: get(["linkedin_url", "linkedin"], 3),
      listing_url: get(["listing_url", "listing"], 8),
      city: get(["city"], 4),
      state: get(["state"], 5),
      brokerage: get(["brokerage"], 6),
      notes: get(["notes"], 7),
    };
  }).filter((r) => r.first_name || r.email);
};

export const OutreachTab = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/outreach/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (selected) {
    return <CampaignDetail id={selected} onBack={() => { setSelected(null); load(); }} />;
  }

  return (
    <div data-testid="admin-outreach">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-3xl text-ink">Cold outreach</h2>
          <p className="text-sm text-ink/55">Listing-hook, success-report & standard campaigns · Resend · CAN-SPAM safe.</p>
        </div>
        <button
          onClick={() => setCreating((v) => !v)}
          data-testid="outreach-new-btn"
          className="inline-flex items-center gap-2 bg-ink text-bone px-5 py-2.5 text-sm font-medium hover:bg-coral transition-colors"
        >
          <Plus className="w-4 h-4" /> New campaign
        </button>
      </div>

      {creating && <CampaignForm onCreated={() => { setCreating(false); load(); }} />}

      <div className="border border-ink/10 divide-y divide-ink/10 mt-4">
        {campaigns.length === 0 && <div className="px-4 py-10 text-center text-ink/40">No campaigns yet.</div>}
        {campaigns.map((c) => (
          <button
            key={c.id}
            onClick={() => setSelected(c.id)}
            data-testid="outreach-campaign-row"
            className="w-full text-left px-4 py-4 flex items-center justify-between hover:bg-ink/[0.03] transition-colors"
          >
            <div>
              <p className="text-ink font-medium">{c.name}</p>
              <p className="text-xs text-ink/50">{c.subject}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-1 ${c.type === "standard" ? "bg-ink/10 text-ink/60" : "bg-coral/15 text-coral"}`}>
                {TYPE_LABEL[c.type] || c.type}
              </span>
              <span className="text-sm text-ink/50">{c.contact_count} contacts</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const CampaignForm = ({ onCreated }) => {
  const [form, setForm] = useState({
    name: "", type: "success_report", sender_name: "Joe from Chateaux", compliance_address: "",
    send_rate_per_hr: 20, ...SUCCESS_REPORT_DEFAULTS,
  });
  const [busy, setBusy] = useState(false);

  const setType = (type) => setForm((f) => ({ ...f, type, ...DEFAULTS_BY_TYPE[type] }));

  const submit = async () => {
    if (!form.name || !form.subject) { toast.error("Name and subject are required."); return; }
    setBusy(true);
    try {
      await api.post("/admin/outreach/campaigns", { ...form, send_rate_per_hr: Number(form.send_rate_per_hr) || 20 });
      toast.success("Campaign created");
      onCreated();
    } catch { toast.error("Could not create campaign."); }
    finally { setBusy(false); }
  };

  const input = "w-full bg-transparent border border-ink/20 px-4 py-3 text-ink focus:border-coral focus:outline-none text-sm";

  return (
    <div className="border border-ink/15 p-6 mb-4 bg-ink/[0.02]" data-testid="outreach-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <input placeholder="Campaign name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="outreach-name" className={input} />
        <select value={form.type} onChange={(e) => setType(e.target.value)} data-testid="outreach-type" className={input}>
          <option value="success_report">Success Report (main app)</option>
          <option value="listing_hook">Listing Hook (self-contained)</option>
          <option value="standard">Standard</option>
        </select>
        <input placeholder="Sender name" value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} className={input} />
        <input placeholder="Compliance address (footer)" value={form.compliance_address} onChange={(e) => setForm({ ...form, compliance_address: e.target.value })} className={input} />
      </div>
      <input placeholder="Subject line" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="outreach-subject" className={`${input} mb-4`} />
      <textarea rows={9} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="outreach-body" className={`${input} mb-4 font-mono text-xs resize-y`} />
      <label className="block text-xs uppercase tracking-[0.15em] text-ink/50 mb-2">LinkedIn DM template</label>
      <textarea rows={4} value={form.linkedin_template} onChange={(e) => setForm({ ...form, linkedin_template: e.target.value })} data-testid="outreach-linkedin" className={`${input} mb-3 font-mono text-xs resize-y`} />
      <div className="flex items-center gap-3 mb-3">
        <label className="text-xs uppercase tracking-[0.15em] text-ink/50">Send rate / hr</label>
        <input type="number" min="1" max="100" value={form.send_rate_per_hr} onChange={(e) => setForm({ ...form, send_rate_per_hr: e.target.value })} className="w-24 bg-transparent border border-ink/20 px-3 py-2 text-ink text-sm" />
      </div>
      <p className="text-xs text-ink/45 mb-4">
        Tokens: {"{{first_name}} {{last_name}} {{market_city}} {{brokerage}} {{share_url}} {{preview_url}} {{listing_address}} {{sender_name}} {{compliance_address}} {{unsubscribe_url}}"}
      </p>
      <button onClick={submit} disabled={busy} data-testid="outreach-create-submit" className="inline-flex items-center gap-2 bg-coral text-bone px-6 py-3 text-sm font-medium hover:bg-ink transition-colors disabled:opacity-60">
        {busy ? "Creating…" : "Create campaign"}
      </button>
    </div>
  );
};

const Metric = ({ label, value }) => (
  <div className="border border-ink/10 p-4">
    <p className="font-serif text-3xl text-ink">{value}</p>
    <p className="text-xs uppercase tracking-[0.12em] text-ink/50 mt-1">{label}</p>
  </div>
);

const CampaignDetail = ({ id, onBack }) => {
  const [data, setData] = useState(null);
  const [csv, setCsv] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [sending, setSending] = useState(false);
  const [prepAll, setPrepAll] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/outreach/campaigns/${id}`).then((r) => setData(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-ink/40 py-10">Loading…</div>;
  const { campaign, contacts } = data;
  const isStandard = campaign.type === "standard";
  const isHook = campaign.type === "listing_hook";
  const prepLabel = isHook ? "Prepare" : "Generate report";

  const metrics = {
    ready: contacts.filter((c) => c.status === "preview_ready").length,
    sent: contacts.filter((c) => c.send_status === "sent").length,
    opened: contacts.filter((c) => c.opened_at).length,
    clicked: contacts.filter((c) => c.clicked_at).length,
  };

  const addContacts = async () => {
    const rows = parseCsv(csv);
    if (rows.length === 0) { toast.error("No valid rows found."); return; }
    try {
      await api.post(`/admin/outreach/campaigns/${id}/contacts`, { contacts: rows });
      toast.success(`Added ${rows.length} contacts`);
      setCsv("");
      load();
    } catch { toast.error("Could not add contacts. Check the format."); }
  };

  const prepare = async (cid) => {
    setBusyId(cid);
    try { await api.post(`/admin/outreach/campaigns/${id}/contacts/${cid}/prepare`); load(); }
    catch { toast.error("Prepare failed."); }
    finally { setBusyId(null); }
  };

  const prepareAll = async () => {
    const pending = contacts.filter((c) => c.status === "pending");
    if (pending.length === 0) { toast.info("Nothing pending to prepare."); return; }
    setPrepAll(true);
    for (const c of pending) {
      try { await api.post(`/admin/outreach/campaigns/${id}/contacts/${c.id}/prepare`); } catch { /* keep going */ }
    }
    setPrepAll(false);
    toast.success("Done preparing pending contacts");
    load();
  };

  const send = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post(`/admin/outreach/campaigns/${id}/send`);
      toast.success(`Sent ${res.sent} · skipped ${res.skipped}${res.throttled ? " · throttled (hourly cap)" : ""}`);
      load();
    } catch { toast.error("Send failed."); }
    finally { setSending(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this campaign and all its contacts?")) return;
    await api.delete(`/admin/outreach/campaigns/${id}`);
    onBack();
  };

  const copyDm = async (c) => {
    const text = renderTokens(campaign.linkedin_template, c, campaign);
    try { await navigator.clipboard.writeText(text); toast.success("LinkedIn DM copied"); }
    catch { toast.error("Copy failed — select manually."); }
  };

  const input = "w-full bg-transparent border border-ink/20 px-4 py-3 text-ink focus:border-coral focus:outline-none text-sm";
  const csvHeader = isHook
    ? "first_name,last_name,email,linkedin_url,,,,,listing_url"
    : "first_name,last_name,email,linkedin_url,city,state,brokerage,notes";

  return (
    <div data-testid="outreach-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-ink/60 hover:text-coral mb-4">
        <ChevronLeft className="w-4 h-4" /> All campaigns
      </button>
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h2 className="font-serif text-3xl text-ink">{campaign.name}</h2>
          <p className="text-sm text-ink/55">{TYPE_LABEL[campaign.type]} · {campaign.subject}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!isStandard && (
            <button onClick={prepareAll} disabled={prepAll} data-testid="outreach-prepare-all" className="inline-flex items-center gap-2 border border-ink/15 px-4 py-2.5 text-sm text-ink hover:border-coral hover:text-coral transition-colors disabled:opacity-60">
              <Sparkles className={`w-4 h-4 ${prepAll ? "animate-pulse" : ""}`} /> {prepAll ? "Preparing…" : isHook ? "Prepare all" : "Generate all"}
            </button>
          )}
          <button onClick={send} disabled={sending} data-testid="outreach-send" className="inline-flex items-center gap-2 bg-coral text-bone px-5 py-2.5 text-sm font-medium hover:bg-ink transition-colors disabled:opacity-60">
            <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send campaign"}
          </button>
          <button onClick={del} data-testid="outreach-delete" className="p-2.5 border border-ink/15 text-ink/50 hover:text-red-600 hover:border-red-300 transition-colors" aria-label="Delete campaign">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="outreach-metrics">
        <Metric label={isHook ? "Previews ready" : "Reports ready"} value={metrics.ready} />
        <Metric label="Sent" value={metrics.sent} />
        <Metric label="Opened" value={metrics.opened} />
        <Metric label="Clicked" value={metrics.clicked} />
      </div>

      <div className="border border-ink/15 p-5 mb-6 bg-ink/[0.02]">
        <label className="block text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">
          Import contacts (CSV: {csvHeader})
        </label>
        <textarea rows={4} value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={isHook ? "Sarah,Chen,sarah@x.com,,,,,,https://zillow.com/homedetails/..." : "Eric,Bramlett,eric@x.com,,Austin,TX,Bramlett Residential,"} data-testid="outreach-csv" className={`${input} font-mono text-xs mb-3`} />
        <button onClick={addContacts} data-testid="outreach-import" className="inline-flex items-center gap-2 bg-ink text-bone px-5 py-2.5 text-sm font-medium hover:bg-coral transition-colors">
          <Upload className="w-4 h-4" /> Import
        </button>
      </div>

      <div className="border border-ink/10 divide-y divide-ink/10">
        {contacts.length === 0 && <div className="px-4 py-10 text-center text-ink/40">No contacts yet.</div>}
        {contacts.map((c) => {
          const shareUrl = c.report_url || c.preview_url;
          return (
            <div key={c.id} className="px-4 py-4 flex items-center gap-4" data-testid="outreach-contact-row">
              {c.staged_photo_url ? (
                <img src={c.staged_photo_url} alt="staged" className="w-16 h-12 object-cover border border-ink/10 shrink-0" />
              ) : (
                <div className="w-16 h-12 bg-ink/5 border border-ink/10 shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-ink text-sm font-medium truncate">
                  {c.first_name} {c.last_name} · {c.email}
                  {c.opened_at && <Eye className="inline w-3.5 h-3.5 text-green-600 ml-2" title="Opened" />}
                  {c.clicked_at && <MousePointerClick className="inline w-3.5 h-3.5 text-coral ml-1" title="Clicked" />}
                </p>
                <p className="text-xs text-ink/50 truncate">
                  {c.address || c.city || c.listing_url || "—"}{c.price ? ` · ${c.price}` : ""}
                  {c.brokerage ? ` · ${c.brokerage}` : ""}
                </p>
                {shareUrl && (
                  <a href={shareUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-coral hover:underline mt-0.5">
                    <ExternalLink className="w-3 h-3" /> {c.report_url ? "Success report" : "Preview page"}
                  </a>
                )}
                {c.error_message && (
                  <p className="inline-flex items-center gap-1 text-xs text-amber-700 mt-0.5"><AlertCircle className="w-3 h-3" /> {c.error_message}</p>
                )}
              </div>
              <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-1 shrink-0 ${STATUS_STYLES[c.send_status === "sent" ? "sent" : c.status] || STATUS_STYLES.pending}`}>
                {(c.send_status === "sent" ? "sent" : c.status || "pending").replace(/_/g, " ")}
              </span>
              {c.linkedin_url && campaign.linkedin_template && (
                <button onClick={() => copyDm(c)} data-testid="outreach-copy-dm" className="inline-flex items-center gap-1.5 border border-ink/15 px-3 py-2 text-xs text-ink hover:border-[#0A66C2] hover:text-[#0A66C2] transition-colors shrink-0" title="Copy LinkedIn DM">
                  <Linkedin className="w-3.5 h-3.5" /> <Copy className="w-3 h-3" />
                </button>
              )}
              {!isStandard && c.send_status !== "sent" && (
                <button
                  onClick={() => prepare(c.id)}
                  disabled={busyId === c.id}
                  data-testid="outreach-prepare"
                  className="inline-flex items-center gap-1.5 border border-ink/15 px-3 py-2 text-xs text-ink hover:border-coral hover:text-coral transition-colors disabled:opacity-50 shrink-0"
                >
                  {busyId === c.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {c.report_slug || c.preview_slug ? "Regenerate" : prepLabel}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
